import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';

interface Option {
  label: string;
  value: string;
  section?: string;
}

interface SearchableDropdownProps {
  options: Option[];
  value: any;
  onChange: (value: string) => void;
  placeholder?: string;
  isInvalid?: boolean;
  className?: string;
}

const DropdownContainer = styled('div')({
  position: 'relative',
  width: '100%'
});

const Input = styled('input')({
  width: '100%',
  padding: '6px 12px',
  fontSize: '1rem',
  border: '1px solid #8596AF', 
  color: '#103C6E',
  borderRadius: '8px',
  '&:focus': {
    outline: 'none',
    borderColor: '#86b7fe',
    boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
  }
});

const DropdownList = styled('ul')({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxHeight: '250px',
  overflowY: 'auto',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  backgroundColor: 'white',
  zIndex: 1000,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
});

const DropdownItem = styled('li')({
  padding: '8px 12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f8f9fa'
  },
  '&.selected': {
    backgroundColor: '#e9ecef'
  }
});

const DropdownSection = styled('li')({
  padding: '8px 12px',
  fontWeight: 'bold',
  backgroundColor: '#f8f9fa',
  borderBottom: '1px solid #dee2e6'
});

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  isInvalid,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Update display value when value changes
    const selectedOption = options.find(opt => opt.value === value);
    setDisplayValue(selectedOption ? selectedOption.label : '');
  }, [value, options]);

  useEffect(() => {
    // Handle clicking outside of dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm(displayValue);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [displayValue]);

  const filteredOptions = options.filter(option => {
    if (!searchTerm) return true;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (option.section?.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (option: Option) => {
    if (option.value === '') return; // Skip section headers
    onChange(option.value);
    setDisplayValue(option.label);
    setSearchTerm(option.label);
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  return (
    <DropdownContainer ref={containerRef} className={className}>
      <Input
        value={isOpen ? searchTerm : displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className="form-control"
        style={{ borderColor: isInvalid ? '#dc3545' : undefined, cursor: isOpen ? 'default' : 'pointer' }}
      />
      {isOpen && (
        <DropdownList>
          {filteredOptions.map((option, index) => (
            option.value === '' ? (
              <DropdownSection key={index}>
                {option.label}
              </DropdownSection>
            ) : (
              <DropdownItem
                key={index}
                onClick={() => handleSelect(option)}
                className={option.value === value ? 'selected' : ''}
              >
                {option.label}
              </DropdownItem>
            )
          ))}
          {filteredOptions.length === 0 && (
            <DropdownItem>אין תוצאות</DropdownItem>
          )}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default SearchableDropdown;